import { useCallback, useContext, useEffect, useState } from "react";
import { RankingsService } from "../../../../../core/http/services/RankingsService";
import { CategoriesService } from "../../../../../core/http/services/CategoriesService";
import AuthContext from "@/contexts/AuthContext";
import { useParams } from "react-router-dom";

export default function useAdminRanking() {
  const { userLoggedData } = useContext(AuthContext);
  const { clubId: clubIdFromUrl } = useParams();
  const clubId = clubIdFromUrl || userLoggedData?.selectedClubId || null;

  const [rankings, setRankings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedRanking, setSelectedRanking] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [standings, setStandings] = useState([]);
  const [loadingRankings, setLoadingRankings] = useState(true);
  const [loadingStandings, setLoadingStandings] = useState(false);

  const fetchCategories = useCallback(async () => {
    if (!clubId) return;
    const { status, data } = await CategoriesService.get(clubId);
    if (status !== 200) return;
    const list = data ?? [];
    setCategories(list);
    if (list.length > 0) setSelectedCategory(list[0].id);
  }, [clubId]);

  const fetchRankings = useCallback(async () => {
    setLoadingRankings(true);
    if (!clubId) {
      setLoadingRankings(false);
      setRankings([]);
      return;
    }

    const { status, data } = await RankingsService.getAll(clubId);
    setLoadingRankings(false);
    if (status !== 200) return;
    const list = data ?? [];
    setRankings(list);
    if (list.length > 0) setSelectedRanking(list[0].id);
  }, [clubId]);

  useEffect(() => {
    fetchRankings();
    fetchCategories();
  }, [fetchRankings, fetchCategories]);

  const fetchStandings = useCallback(async (rankingId, categoryId) => {
    if (!rankingId || !categoryId) return;
    if (!clubId) return;
    setLoadingStandings(true);
    const { status, data } = await RankingsService.getStandings({
      clubId,
      rankingId,
      categoryId,
    });
    setLoadingStandings(false);
    if (status !== 200) {
      setStandings([]);
      return;
    }
    const raw = Array.isArray(data) ? data : (data?.standings ?? []);
    setStandings(
      raw.map((entry, index) => ({
        id: entry.playerId ?? entry.player?.id ?? entry.id ?? String(index),
        name: entry.playerName ?? entry.player?.name ?? entry.name ?? "—",
        points: entry.totalPoints ?? entry.points ?? 0,
        avatarUrl: entry.avatarUrl ?? entry.player?.avatarUrl ?? null,
        position: entry.position ?? index + 1,
        wins: entry.wins ?? 0,
        losses: entry.losses ?? 0,
        matchesPlayed: entry.matchesPlayed ?? 0,
        saldo: entry.saldo ?? 0,
      })),
    );
  }, [clubId]);

  useEffect(() => {
    fetchStandings(selectedRanking, selectedCategory);
  }, [selectedRanking, selectedCategory, fetchStandings]);

  return {
    rankings,
    categories,
    selectedRanking,
    selectedCategory,
    standings,
    loadingRankings,
    loadingStandings,
    setSelectedRanking,
    setSelectedCategory,
  };
}
